import { Vue } from 'vue-property-decorator';
import { LoggerService } from '@/utils';

export class Helpers extends Vue {
    /**
     * Method to generate a kebab case identifier out of a name and prefixe
     * @param name      The name to convert
     * @param prefix    A prefix string
     */
    public static getReferenceName(name: string, prefix?: string): string {
        return (prefix !== undefined ? `${prefix}-` : '') + `${name.toLowerCase().replace(/\s/g, '-')}`;
    }

    /**
     * Method to get the reference for a dynamic set component reference
     * @param $refs             The Vue $refs object, normaly from this.$refs in a component
     * @param refName           The name of the reference
     */
    public static getReference<T>($refs: any, refName: string): T {
        const descriptor = Object.getOwnPropertyDescriptor($refs, refName);
        let result!: T;
        if (descriptor !== undefined) {
            if (descriptor.value) {
                result = descriptor.value[0] !== undefined ? descriptor.value[0] : descriptor.value;
            } else if (descriptor.get) {
                result = descriptor.get()[0] !== undefined ? descriptor.get()[0] : descriptor.get();
            } else {
                LoggerService.LogError(`Something went wrong on getting Reference ${refName}`);
            }
        } else {
            LoggerService.LogWarn(`Couldn't find Reference ${refName}. Maybe not rendered yet.`);
        }

        return result;
    }

    /**
     * Method to comparte two dates from date strings.
     * Returns 0 if both arte the same, 1 if lhs is greater than rhs
     * and -1 if 1 if lhs is lower than rhs
     * @param lhsDateString     A date string
     * @param rhsDateString     A date string
     */
    public static compareDateStrings(lhsDateString: string, rhsDateString: string): number {
        let ret = 0;
        const lhsDate: Date = new Date(lhsDateString);
        const rhsDate: Date = new Date(rhsDateString);

        if (lhsDate < rhsDate) {
            ret = -1;
        } else if (lhsDate > rhsDate) {
            ret = 1;
        }

        return ret;
    }

    /**
     * Method to get a formatted date string from an iso date string
     * @param inDateString ISO date string
     * @param inISOFormat  ISO format string, default is "en-GB"
     */
    public static getFormattedDateString(inDateString: string, inISOFormat: string = 'en-GB'): string {
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        };
        return (new Date(inDateString)).toLocaleDateString(inISOFormat, options);
    }

    /**
     * Method to get a formatted time string from an iso date string
     * @param inDateString ISO date string
     * @param inISOFormat  ISO format string, default is "en-GB"
     */
    public static getFormattedTimeString(inDateString: string, inISOFormat: string = 'en-GB', withSeconds: boolean = false): string {
        const options: Intl.DateTimeFormatOptions = {
            hour: 'numeric',
            minute: 'numeric',
        };
        if (withSeconds === true) {
            options.second = 'numeric';
        }
        return (new Date(inDateString)).toLocaleTimeString(inISOFormat, options);
    }

    /**
     * Method to get a formatted date time string from an iso date string
     * @param inDateString ISO date string
     * @param inISOFormat  ISO format string, default is "en-GB"
     */
    public static getExportDateTimeString(inDateString: string, inISOFormat: string = 'en-GB', withSeconds: boolean = true): string {
        return `${this.getFormattedDateString(inDateString, inISOFormat)} ${this.getFormattedTimeString(inDateString, inISOFormat, withSeconds)}`;
    }

    /**
     * Method to check if a string is undefined or just empty
     * @param inString String to test
     */
    public static undef(inString?: string) {
        let isUndefined = false;

        if (inString == null || inString === '' || inString.length === 0) {
            isUndefined = true;
        }

        return isUndefined;
    }
}
